import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { Star, Shield, Edit3, Check, Trash2 } from 'lucide-react';
import { isEntreprise, isEntreprisePending } from '@/utils/entreprise';
import BecomeCompanyCard from '@/components/BecomeCompanyCard';
import { BACKEND_URL } from '@/config';
import { roleLabel, statusInfo } from '@/utils/statusLabels';
import VerificationBadges from '@/components/VerificationBadges';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

export default function ProfilePage() {
  const { id } = useParams();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isMyProfile = !id || id === user?.id;

  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (isMyProfile) {
          const { data } = await axios.get(`${API}/users/me`);
          setProfile(data);
          setForm({ first_name: data.first_name || '', last_name: data.last_name || '', bio: data.bio || '', phone: data.phone || '', company_name: data.company_name || '', siren: data.siren || data.siret || '' });
        } else {
          const { data } = await axios.get(`${API}/users/${id}`);
          setProfile(data);
          setRatings(data.recent_ratings || []);
        }
      } catch { toast.error('Profil introuvable'); }
      finally { setLoading(false); }
    };
    if (isMyProfile) {
      setRatings([]);
      axios.get(`${API}/ratings/user/${user?.id}`).then(r => setRatings(r.data || [])).catch(() => {});
    }
    load();
  }, [id, isMyProfile, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me`);
      toast.success('Compte supprimé. Vos données ont été anonymisées.');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression du compte');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me`, form);
      updateUser(form);
      setProfile(p => ({ ...p, ...form }));
      toast.success('Profil mis à jour.');
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const getAvatarClass = (p) => {
    if (!p) return 'av-navy';
    if (p.roles?.includes('ROLE_ADMIN')) return 'av-red';
    if (p.roles?.includes('ROLE_SENDER') && p.roles?.includes('ROLE_CARRIER')) return 'av-violet';
    if (p.roles?.includes('ROLE_CARRIER')) return 'av-gold';
    return 'av-teal';
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="skeleton" style={{ height: 300 }} /></div>;
  if (!profile) return <div className="empty-state"><div>Profil introuvable</div></div>;

  const initials = `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div className="c2s-topbar">
        <div className="c2s-topbar-title">{isMyProfile ? 'Mon profil' : 'Profil utilisateur'}</div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 20 }}>
          <div className={`av xl ${getAvatarClass(profile)}`}>{initials}</div>
          <div style={{ flex: 1 }}>
            {editing ? (
              <div>
                <div className="field-row" style={{ marginBottom: 12 }}>
                  <input className="field-input" placeholder="Prénom" value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} />
                  <input className="field-input" placeholder="Nom" value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} />
                </div>
                <input className="field-input" placeholder="Téléphone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} style={{ marginBottom: 4 }} data-testid="input-phone" />
                <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginBottom: 8 }} data-testid="profile-phone-note">
                  {profile.phone_verified
                    ? 'Numéro vérifié. Un autre numéro devra être vérifié de nouveau : changez-le plutôt dans Mes vérifications.'
                    : 'Le numéro se vérifie dans Mes vérifications, avec un code reçu par WhatsApp ou SMS.'}
                </div>
                {profile.profile_type === 'PROFESSIONAL' && (
                  <>
                    <input className="field-input" placeholder="Nom société" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} style={{ marginBottom: 8 }} />
                    <input className="field-input" placeholder="SIRET / SIREN" value={form.siren} onChange={e => setForm(p => ({ ...p, siren: e.target.value }))} style={{ marginBottom: 4 }} data-testid="input-siren" />
                    <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginBottom: 8 }}>
                      Vérifié par l'équipe Colis2Ship. Modifier le nom ou le numéro relance la vérification.
                    </div>
                  </>
                )}
                <textarea className="field-textarea" placeholder="Bio..." value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={2} style={{ marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Annuler</button>
                  <button data-testid="btn-save-profile" className="btn btn-gold btn-sm" disabled={saving} onClick={handleSave}>
                    {saving ? '...' : <><Check size={14} /> Enregistrer</>}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                  <h2 style={{ fontFamily: 'var(--f-display)', fontSize: 22, fontWeight: 800, margin: 0 }}>{profile.first_name} {profile.last_name}</h2>
                  <VerificationBadges user={profile} />
                  {isEntreprise(profile) && <span className="badge badge-entreprise">Entreprise</span>}
                  {isMyProfile && isEntreprisePending(profile) && (
                    <span className="badge badge-entreprise-pending" title="L'équipe Colis2Ship vérifie votre SIRET" data-testid="badge-entreprise-pending">
                      Entreprise · en vérification
                    </span>
                  )}
                  {profile.pro_subscribed && <span className="badge badge-pro" title="Transporteur abonné PRO">PRO</span>}
                  {isMyProfile && <button data-testid="btn-edit-profile" className="btn btn-ghost btn-icon btn-sm" onClick={() => setEditing(true)}><Edit3 size={14} /></button>}
                </div>
                {profile.company_name && profile.profile_type === 'PROFESSIONAL' && <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 4 }}>{profile.company_name}</div>}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  {profile.avg_rating && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {[1,2,3,4,5].map(s => <span key={s} className={`star${s <= Math.round(profile.avg_rating) ? ' filled' : ''}`}>★</span>)}
                      <span style={{ fontSize: 14, fontWeight: 700, marginLeft: 4 }}>{profile.avg_rating}</span>
                      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>({profile.rating_count} avis)</span>
                    </div>
                  )}
                </div>
                {profile.bio && <div style={{ fontSize: 14, color: 'var(--ink2)', lineHeight: 1.6 }}>{profile.bio}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {profile.roles?.map(r => (
                    <span key={r} className={`badge ${r === 'ROLE_ADMIN' ? 'badge-admin' : r === 'ROLE_CARRIER' ? 'badge-trajet' : 'badge-colis'}`}>
                      {roleLabel(r)}
                    </span>
                  ))}
                  {profile.direct_missions_count > 0 && (
                    <span className="badge" style={{ background: 'rgba(245,208,128,.15)', color: '#b8860b', border: '1px solid rgba(245,208,128,.4)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>
                      {profile.direct_missions_count} réservation{profile.direct_missions_count > 1 ? 's' : ''} directe{profile.direct_missions_count > 1 ? 's' : ''}
                    </span>
                  )}
                  {isMyProfile && <span className="badge" style={{ background: 'var(--cream)', color: 'var(--ink2)', border: '1px solid rgba(15,23,42,.12)' }}>{profile.email}</span>}
                </div>
              </div>
            )}
          </div>
        </div>

        {profile.kyc_status && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: profile.kyc_status === 'VERIFIED' ? 'var(--tbg)' : 'var(--warm)', borderRadius: 'var(--r)' }}>
            <Shield size={16} color={profile.kyc_status === 'VERIFIED' ? 'var(--teal)' : 'var(--gold)'} />
            <div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Pièce d'identité : {statusInfo('identite', profile.kyc_status).label.toLowerCase()}</span>
              {profile.kyc_status !== 'VERIFIED' && isMyProfile && (
                <div style={{ fontSize: 12, color: 'var(--ink2)' }}>
                  <button style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontWeight: 600, fontSize: 12, padding: 0 }}
                    onClick={() => navigate('/app/kyc')}>
                    Compléter la vérification →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isMyProfile && (
        <BecomeCompanyCard
          profile={profile}
          onDone={(data) => {
            setProfile(p => ({ ...p, ...data }));
            setForm(f => ({ ...f, company_name: data.company_name || '', siren: data.siren || '' }));
            updateUser({ profile_type: data.profile_type, company_name: data.company_name, siren: data.siren, company_verified: false });
          }}
        />
      )}

      {isMyProfile && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'var(--f-display)', fontSize: 15, fontWeight: 700, marginBottom: 12, color: '#7f1d1d' }}>
            Zone de danger
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 12 }}>
            La suppression de votre compte est définitive. Vos données personnelles seront anonymisées conformément au RGPD. Vos missions et historique resteront sous forme anonyme.
          </div>
          <button
            data-testid="btn-delete-account"
            className="btn btn-danger btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => { setDeleteConfirm(''); setShowDeleteModal(true); }}
          >
            <Trash2 size={14} /> Supprimer mon compte
          </button>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div className="modal-title" style={{ color: '#7f1d1d' }}>Supprimer mon compte</div>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, marginBottom: 16, color: 'var(--ink)' }}>
                Cette action est <strong>irréversible</strong>. Vos données personnelles (nom, email, téléphone, KYC) seront anonymisées. Vos missions actives doivent être finalisées ou annulées avant.
              </p>
              <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 8 }}>
                Tapez <strong>SUPPRIMER</strong> pour confirmer :
              </div>
              <input
                className="field-input"
                placeholder="SUPPRIMER"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                data-testid="input-delete-confirm"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button
                data-testid="btn-confirm-delete-account"
                className="btn btn-danger"
                disabled={deleteConfirm !== 'SUPPRIMER' || deleting}
                onClick={handleDeleteAccount}
              >
                {deleting ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ratings.length > 0 && (
        <div className="card">
          <div className="section-title">Avis ({ratings.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {ratings.map(r => (
              <div key={r.id} style={{ display: 'flex', gap: 12, padding: 12, background: 'var(--cream)', borderRadius: 'var(--r)' }}>
                <div className="av sm av-teal">{(r.author?.first_name || '?')[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{r.author?.first_name} {r.author?.last_name}</span>
                    <div className="stars">{[1,2,3,4,5].map(s => <span key={s} className={`star${s <= r.score ? ' filled' : ''}`} style={{ fontSize: 12 }}>★</span>)}</div>
                  </div>
                  {r.comment && <div style={{ fontSize: 13, color: 'var(--ink2)' }}>{r.comment}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
